<template>
  <!-- divide-y сам ставит разделитель между каждой видимой строкой (скрытые v-if в DOM не попадают) -->
  <div
    class="basket-summary p-4 mb-4 divide-y divide-gray-200 dark:divide-gray-700"
    data-testid="basket-summary"
  >
    <!-- Общая сумма до списания баллов -->
    <div class="flex justify-between items-center py-2">
      <div class="flex items-center gap-1 text-gray-600 dark:text-gray-300">
        <UIcon name="i-heroicons-shopping-cart" class="w-4 h-4" />
        <span>Сумма заказа:</span>
      </div>
      <span class="font-medium" data-testid="summary-cart-total">{{ safePriceBreakdown.cartTotal.formatted }}</span>
    </div>

    <!-- Списываемые баллы -->
    <div
      v-if="safePriceBreakdown.pointsDiscount.show"
      class="flex justify-between items-center py-2"
    >
      <div class="flex items-center gap-1 text-gray-600 dark:text-gray-300">
        <UIcon name="i-ep-coin" class="w-4 h-4" />
        <span>{{ safePriceBreakdown.pointsDiscount.label }}:</span>
      </div>
      <span class="font-medium text-red-500" data-testid="summary-points-discount">
        {{ safePriceBreakdown.pointsDiscount.formatted }}
      </span>
    </div>

    <!-- Скидка по промокоду -->
    <div
      v-if="safePriceBreakdown.promocodeDiscount.show"
      class="flex justify-between items-center py-2"
    >
      <div class="flex items-center gap-1 text-gray-600 dark:text-gray-300">
        <UIcon name="i-heroicons-ticket" class="w-4 h-4" />
        <span>{{ safePriceBreakdown.promocodeDiscount.label }}:</span>
      </div>
      <span class="font-medium text-green-500" data-testid="summary-promocode-discount">
        {{ safePriceBreakdown.promocodeDiscount.formatted }}
      </span>
    </div>

    <!-- Приветственная скидка на первые заказы -->
    <div
      v-if="safePriceBreakdown.onboardingDiscount.show"
      class="flex justify-between items-center py-2"
    >
      <div class="flex items-center gap-1 text-gray-600 dark:text-gray-300">
        <UIcon name="i-heroicons-gift" class="w-4 h-4" />
        <span>{{ safePriceBreakdown.onboardingDiscount.label }}:</span>
      </div>
      <span class="font-medium text-green-500" data-testid="summary-onboarding-discount">
        {{ safePriceBreakdown.onboardingDiscount.formatted }}
      </span>
    </div>

    <!-- Возвращаемые баллы (кешбэк) -->
    <div
      v-if="isLoyaltyEnabled && safeLoyaltyInfo.cashbackAmount > 0"
      class="flex justify-between items-center py-2"
    >
      <div class="flex items-center gap-1 text-gray-600 dark:text-gray-300">
        <UIcon name="i-ep-coin" class="w-4 h-4" />
        <span>Вернётся баллами:</span>
      </div>
      <span class="font-medium text-green-500" data-testid="summary-cashback">
        +{{ safeLoyaltyInfo.cashbackAmount }} ₽
      </span>
    </div>

    <!--Доставка с учетом времени и суммы-->
    <div
      v-if="safePriceBreakdown.deliveryCost.show"
      class="flex justify-between items-center py-2"
    >
      <div class="flex items-center gap-1 text-gray-600 dark:text-gray-300">
        <UIcon name="i-hugeicons-truck-delivery" class="w-4 h-4" />
        <span>{{ safePriceBreakdown.deliveryCost.label }}:</span>
      </div>
      <span class="font-bold text-md" data-testid="summary-delivery-cost">{{ safePriceBreakdown.deliveryCost.formatted }}</span>
    </div>

    <!-- Сервисный сбор -->
    <div
      v-if="safePriceBreakdown.serviceFee.show"
      class="flex justify-between items-center py-2"
    >
      <div class="flex items-center gap-1 text-gray-600 dark:text-gray-300">
        <UIcon name="i-mdi-percent" class="w-4 h-4" />
        <span>{{ safePriceBreakdown.serviceFee.label }}:</span>
      </div>
      <span class="font-bold text-md" data-testid="summary-service-fee">{{ safePriceBreakdown.serviceFee.formatted }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, unref } from 'vue'

interface Props {
  priceBreakdown: any
  formattedLoyaltyInfo: any
  isLoyaltyEnabled: boolean
}

const props = defineProps<Props>()

// ✅ Безопасный доступ к priceBreakdown с дефолтными значениями
const safePriceBreakdown = computed(() => {
  const breakdown = unref(props.priceBreakdown)
  
  if (!breakdown || typeof breakdown !== 'object') {
    return {
      cartTotal: { formatted: '0 ₽', show: false },
      pointsDiscount: { formatted: '0 ₽', label: '', show: false },
      promocodeDiscount: { formatted: '0 ₽', label: '', show: false },
      onboardingDiscount: { formatted: '0 ₽', label: '', show: false },
      deliveryCost: { formatted: '0 ₽', label: '', show: false },
      serviceFee: { formatted: '0 ₽', label: '', show: false },
    }
  }

  // Фолбэк для onboardingDiscount: локальный priceBreakdown без preview его не содержит
  return { onboardingDiscount: { formatted: '0 ₽', label: '', show: false }, ...breakdown }
})

// ✅ Безопасный доступ к formattedLoyaltyInfo
const safeLoyaltyInfo = computed(() => {
  const info = unref(props.formattedLoyaltyInfo)
  
  if (!info || typeof info !== 'object') {
    return {
      cashbackAmount: 0,
      availablePoints: 0,
      cashbackLevel: '0%',
    }
  }
  
  return info
})
</script>

<style scoped lang="scss">
.basket-summary {
  @apply p-4 mb-4;
}
</style>